import { ChangeDetectionStrategy, Component, ElementRef, HostBinding, Input } from '@angular/core';
import { syncButtonNameFromHost } from 'directives/cs-capture-cover/host-name-source';
import { TranslatedString, untranslatedString } from 'models/translated-string';

@Component({
  selector: 'cs-bar-button',
  templateUrl: 'cs-bar-button.html',
  host: { 'class': 'bar-button' },
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CsBarButton {
  @Input() icon: string = '';
  @Input() label: TranslatedString = untranslatedString('');

  /**
   * A second, quieter line under the label, saying in a few words what this button is for.
   *
   * Optional, and drawn only when it has something in it, so a button given no description is
   * the plain icon-over-label box this component has always been. The meal screen's add-routes
   * grid uses it to say in a few words what each route does (#4969).
   */
  @Input() description: TranslatedString | undefined;

  /**
   * What a screen reader announces for this button, when the drawn text is not the right name.
   *
   * The label and the description are drawn on the HOST, outside the <button> element that
   * actually takes the focus. Without this input that button takes its name from the host's
   * drawn text through `aria-labelledby` (see `syncButtonNameFromHost`), so the label and the
   * description are read out. Give it the full name of the thing the button does when the drawn
   * label is not that: a button whose label had to be shortened to fit still says its whole
   * name here.
   */
  @Input() ariaLabel?: string;

  @Input() disabled: boolean = false;

  @HostBinding('attr.disabled')
  get disabledAttr() { return this.disabled ? '' : null; }

  constructor(private elementRef: ElementRef) {}

  ngAfterViewChecked() {
    // the inner <button> is empty, so without an ariaLabel it takes its name from the drawn text
    syncButtonNameFromHost(this.elementRef, this.ariaLabel);
  }
}
